import AuthenticationService from "@app/authentication/authenticationService";
import { querystringify } from "@app/shared/utility/querystringify";
import { XhrReadyStateEnum } from "@app/shared/xhrReadyStateEnum";

function bindAbortSignal(xhr: JQuery.jqXHR, signal?: AbortSignal): void {
  if (!signal) return;
  if (signal.aborted) {
    xhr.abort();
    return;
  }
  signal.addEventListener("abort", () => xhr.abort(), { once: true });
}

/** The base class for all services. A service interacts with our API. */
export default abstract class ServiceBase {
  /** The versioned endpoint used in all requests. */
  protected apiEndpoint = window.apiEndpointV2_0;

  protected authenticationEndpoint = window.authEndpoint;

  /** Builds an object for user in setting the headers in each request. */
  protected headers(): { [key: string]: string } {

    const user = AuthenticationService.getInstance().getUser();

    return {
      "Authorization": `Bearer ${user ? user.access_token : null}`,
      "Cache-Control": "no-store",
      "Pragma": "no-cache",
      "X-Timezone": Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
  }

  /**
   * Performs a GET request to a particular URL.
   * @param url The URL to target.
   * @param query The query params to use
   * @param endpoint
   * @param disableAutomaticSerialization Whether to disable automatic query-string serialization. Defaults to false.
   * @param xhrFields
   * @param signal Optional AbortSignal that will call xhr.abort() when fired.
   */
  protected async makeGet<T>(url: string, query?: unknown, endPoint?: string, disableAutomaticSerialization = false, xhrFields?: unknown, signal?: AbortSignal): Promise<T> {
    if (query && !disableAutomaticSerialization && typeof query === "object") {
      query = querystringify(query as { [key: string]: unknown });
    }

    const headers = this.headers();

    const x = $.get({
      contentType: "application/json",
      headers,
      url: this.makeUrl(url, endPoint, query as string),
      xhrFields,
    });

    bindAbortSignal(x, signal);
    return x;
  }

  /**
   * Performs a GET request to a particular URL, passing any authentication cookies with the request.
   * @param url
   * @param endPoint
   * @param query
   * @param disableAutomaticSerialization
   */
  protected async makeGetWithCredentials<T>(url: string, query?: unknown, disableAutomaticSerialization = false, endPoint?: string): Promise<T> {
    if (query && !disableAutomaticSerialization && typeof query === "object") {
      query = querystringify(query as { [key: string]: unknown });
    }

    const x: T = await $.get({
      contentType: "application/json",
      headers: {
        "Cache-Control": "no-store",
        "Pragma": "no-cache",
      },
      url: this.makeUrl(url, endPoint, query as string),
      xhrFields: { withCredentials: true },
    });

    return x;
  }

  protected async makeOptionalGet<T>(url: string, query?: unknown, disableAutomaticSerialization = false, endpoint?: string): Promise<Maybe<T>> {
    if (query && !disableAutomaticSerialization && typeof query === "object") {
      query = querystringify(query as { [key: string]: unknown });
    }
    const headers = this.headers();

    return new Promise<Maybe<T>>((resolve, reject) => {
      $.get({
        contentType: "application/json",
        headers,
        url: this.makeUrl(url, endpoint, query as string),
      }).then((result: T) => {
        resolve(result);
      }, (xhr, _, error) => {
        if (xhr.status === 404) {
          resolve();
        }

        reject(error);
      });
    });
  }

  /**
   * Performs a POST request to a particular URL, providing a designated body.
   * Will by default JSON serialize the body, but this can be turned off.
   * @param url The URL to target.
   * @param body The body to send. Optional.
   * @param disableAutomaticSerialization Whether to disable automatic JSON serialization. Defaults to false.
   * @param signal Optional AbortSignal that will call xhr.abort() when fired.
   */
  protected async makePost<T>(url: string, body?: unknown, endPoint?: string, disableAutomaticSerialization = false, contentType: string | false = "application/json", processData = true, xhrFields?: unknown, signal?: AbortSignal): Promise<T> {
    if (body && !disableAutomaticSerialization) {
      body = JSON.stringify(body);
    }

    const headers = this.headers();
    const xhr = $.post({
      contentType,
      data: body,
      headers,
      processData,
      url: this.makeUrl(url, endPoint),
      xhrFields,
    });

    bindAbortSignal(xhr, signal);
    return xhr;
  }

  /**
   * Performs a POST request to a particular URL, providing a designated body.
   * Will by default JSON serialize the body, but this can be turned off.
   * This method returns a jquery jqXHR object instead of a promise (by not awaiting the response).
   * That allows us to call the XHR.abort() method on it to cancel an open request,
   * while offering the same async functionality as a Promise.
   * The methods are separate to appease the type safety gods.
   * @param url The URL to target.
   * @param body The body to send. Optional.
   * @param disableAutomaticSerialization Whether to disable automatic JSON serialization. Defaults to false.
   *
   * @deprecated - This method is deprecated in favor of makePost with an AbortSignal parameter;
   */
  protected makeAbortablePost<T>(url: string, body?: unknown, endPoint?: string, disableAutomaticSerialization = false, contentType: string | false = "application/json", processData = true, xhrFields?: unknown): JQuery.jqXHR<T> {
    if (body && !disableAutomaticSerialization) {
      body = JSON.stringify(body);
    }

    const headers = this.headers();
    const x = $.post({
      contentType,
      data: body,
      headers,
      processData,
      url: this.makeUrl(url, endPoint),
      xhrFields,
    });

    return x;
  }

  /**
   * Performs a GET request to a particular URL.
   * This method returns a jquery jqXHR object instead of a promise (by not awaiting the response).
   * That allows us to call the XHR.abort() method on it to cancel an open request,
   * while offering the same async functionality as a Promise.
   * The methods are separate to appease the type safety gods.
   * @param url The URL to target.
   * @param query The query params to use.
   * @param disableAutomaticSerialization Whether to disable automatic query-string serialization. Defaults to false.
   *
   * @deprecated - This method is deprecated in favor of makeGet with an AbortSignal parameter;
   */
  protected makeAbortableGet<T>(url: string, query?: unknown, endPoint?: string, disableAutomaticSerialization = false, xhrFields?: unknown): JQuery.jqXHR<T> {
    if (query && !disableAutomaticSerialization && typeof query === "object") {
      query = querystringify(query as { [key: string]: unknown });
    }

    const headers = this.headers();
    const x = $.get({
      contentType: "application/json",
      headers,
      url: this.makeUrl(url, endPoint, query as string),
      xhrFields,
    });

    return x;
  }

  /**
   * Performs a PUT request to a particular URL, providing a designated body.
   * Will by default JSON serialize the body, but this can be turned off.
   * @param url The URL to target.
   * @param body The body to send. Optional.
   * @param disableAutomaticSerialization Whether to disable automatic JSON serialization. Defaults to false.
   */
  protected async makePut<T = void>(url: string, body?: unknown, endPoint?: string, disableAutomaticSerialization = false, contentType = "application/json"): Promise<T> {
    if (body && !disableAutomaticSerialization) {
      body = JSON.stringify(body);
    }

    const headers = this.headers();

    const x: T = await $.ajax({
      contentType: contentType,
      data: body,
      headers,
      method: "PUT",
      url: this.makeUrl(url, endPoint),
    });

    return x;
  }

  /**
   * Performs a PATCH request to a particular URL, providing a designated body.
   * Will by default JSON serialize the body, but this can be turned off.
   * @param url The URL to target.
   * @param body The body to send. Optional.
   * @param disableAutomaticSerialization Whether to disable automatic JSON serialization. Defaults to false.
   * @param timeoutSeconds The timeout for this request. Defaults to 300 seconds.
   */
  protected async makePatch<T = void>(url: string, body?: unknown, disableAutomaticSerialization = false, endPoint?: string, timeoutSeconds = 300): Promise<T> {
    if (body && !disableAutomaticSerialization) {
      body = JSON.stringify(body);
    }

    const headers = this.headers();

    const x: T = await $.ajax({
      contentType: "application/json",
      data: body,
      headers,
      method: "PATCH",
      timeout: timeoutSeconds * 1000, // value is in MS
      url: this.makeUrl(url, endPoint),
    });

    return x;
  }

  /**
   * Performs a DELETE request to a particular URL, providing a designated body.
   * Will by default JSON serialize the body, but this can be turned off.
   * @param url The URL to target.
   * @param body The body to send. Optional.
   * @param disableAutomaticSerialization Whether to disable automatic JSON serialization. Defaults to false.
   */
  protected async makeDelete<T = void>(url: string, body?: unknown, endPoint?: string): Promise<T> {
    if (body) {
      body = JSON.stringify(body);
    }

    const headers = this.headers();
    const x: T = await $.ajax({
      contentType: "application/json",
      data: body,
      headers,
      method: "DELETE",
      url: this.makeUrl(url, endPoint),
    });

    return x;
  }

  /**
   * Creates a full URL for usage in API calls.
   * If no endpoint is provided then the base class' default is used.
   * @param targetRoute The route we're targeting.
   * @param endpoint The API endpoint we're targeting, defaults to the base class' apiEndpoint.
   * @param query [optional] The query to be used
   */
  private makeUrl(targetRoute: string, endpoint: string = this.apiEndpoint, query?: unknown) {
    // If the provided route doesn't begin with a slash, add it.
    if (targetRoute.charAt(0) !== "/") {
      targetRoute = `/${targetRoute}`;
    }
    if (query) {
      if (typeof query !== "string" || query.charAt(0) !== "?") {
        targetRoute = `${targetRoute}?`;
      }
      targetRoute = `${targetRoute}${query}`;
    }

    return `${endpoint}${targetRoute}`;
  }

  /**
   * This method can be passed to any jqXHR object's .catch method to handle the inevitable error caused by .abort()
   * @param e The jqXHR object autogenerated by .catch
   * @returns void, but logs an error if something goes wrong
   */
  public handleAbortConnection(e: JQuery.jqXHR): void {
    if (e?.statusText === "abort" || e?.readyState === XhrReadyStateEnum.UnsentOrAborted || e?.status === 0) {
      return;
    }
    // browsers will automatically log errors
  }
}
